import { kebabCase, memoize } from 'lodash-es';
import type { KebabCasedProperties } from 'type-fest';
import { computed, type ComputedRef } from 'vue';

/**
 * Memoized kebab-case converter for prop keys.
 * Since prop keys are static strings, memoizing avoids repeated conversions.
 */
const toKebab = memoize(kebabCase);

type Options = {
  /**
   * Array of prop keys to exclude from the output.
   * Useful for Vue-only props that shouldn't be passed to the web component.
   * Use `as const` for type-safe exclusion: `{ exclude: ['name', 'modelValue'] as const }`
   */
  exclude?: readonly string[];
  /**
   * Whether to convert camelCase keys to kebab-case.
   * @default true
   */
  kebabCase?: boolean;
};

/**
 * Converts Vue props object to web component attributes with kebab-case keys.
 *
 * **When to use:** Only use this composable when you observe props not being passed
 * correctly to web components. Don't use it by default.
 *
 * **The problem:** In some cases, when props are spread programmatically via
 * `v-bind="$props"`, Vue converts camelCase keys to lowercase (e.g., `hideacceptbutton`)
 * instead of kebab-case (`hide-accept-button`). Stencil web components expect
 * kebab-case attributes, causing the props to be ignored.
 *
 * **Why it happens:** The exact reason why Vue behaves inconsistently is unclear.
 * In some contexts (e.g., playground pages), props are set as DOM properties and work
 * correctly. In other contexts (e.g., programmatically rendered components), props are
 * set as lowercase attributes and fail. If you encounter this issue, use this composable
 * to ensure proper attribute naming.
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * const props = defineProps<{ hideAcceptButton: boolean; paneType: string; name: string }>();
 * // Use `as const` for type-safe exclusion - excluded keys won't appear in the return type
 * const webComponentProps = useWebComponentProps(props, { exclude: ['name'] as const });
 * </script>
 *
 * <template>
 *   <sdf-focus-pane v-bind="webComponentProps" />
 * </template>
 * ```
 *
 * @param props - The Vue component props object
 * @param options - Optional configuration
 * @param options.exclude - Array of prop keys to exclude from the output
 * @param options.kebabCase - Whether to convert keys to kebab-case (default: true)
 * @returns A computed object suitable for web component v-bind
 */
type UseWebComponentProps = {
  // Overload: kebabCase is false with exclusions
  <T extends Record<string, unknown>, E extends readonly string[]>(
    props: T,
    options: { exclude: E; kebabCase: false }
  ): ComputedRef<Omit<T, E[number]>>;

  // Overload: kebabCase is false without exclusions
  <T extends Record<string, unknown>>(props: T, options: { kebabCase: false }): ComputedRef<T>;

  // Overload: kebabCase is true with exclusions
  <T extends Record<string, unknown>, E extends readonly string[]>(
    props: T,
    options: { exclude: E; kebabCase?: true }
  ): ComputedRef<KebabCasedProperties<Omit<T, E[number]>>>;

  // Overload: kebabCase is true without exclusions
  <T extends Record<string, unknown>>(props: T, options: { kebabCase?: true }): ComputedRef<KebabCasedProperties<T>>;

  // Overload: no options - return kebab-cased keys
  <T extends Record<string, unknown>>(props: T): ComputedRef<KebabCasedProperties<T>>;
};

export const useWebComponentProps: UseWebComponentProps = (props: Record<string, unknown>, options = {}) => {
  const { exclude = [], kebabCase: useKebabCase = true } = options as Options;

  return computed(() => {
    const res: Record<string, unknown> = {};

    for (const key in props) {
      if (!exclude.includes(key)) {
        res[useKebabCase ? toKebab(key) : key] = props[key];
      }
    }

    return res;
  });
};
